import type { CeoCadence, CeoCadenceItem, CeoCockpitResponse } from '../ceoCockpitTypes'
import type { CeoSurface } from '../ceoCockpitUrlState'
import { chipLabelFor, chipNumberFor } from '../ceoCadenceModel'

export type CeoNavStripProps = {
  payload: CeoCockpitResponse
  cadence: CeoSurface
  onCadenceChange: (cadence: CeoSurface) => void
  /** Switches to the item's cadence and scrolls to its card. */
  onJumpToItem: (cadence: CeoCadence, key: string) => void
}

const TABS: Array<{ id: CeoCadence; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
]

/**
 * One navigation, not two.
 *
 * The tab and the chips beneath it are the same thing — a rhythm and what it is
 * carrying — so stacking them as separate rows made a reader parse the page
 * twice to answer one question. It scrolls sideways rather than wrapping, so
 * the count of things needing attention is never disguised by how wide the
 * window happens to be.
 *
 * On Explore the chips fold away: that tab asks the diagnostic question, and
 * carrying nine unrelated numbers into it is the clutter this page exists to
 * remove.
 */
export function CeoNavStrip({ payload, cadence, onCadenceChange, onJumpToItem }: CeoNavStripProps) {
  const cadences = payload.cadences
  // Explore asks a different question from the rhythms, so its chips fold.
  // Setup remains link-addressable from Settings, but is deliberately not a
  // fifth cadence: the approved cockpit has four doors and a CEO should not
  // have to parse configuration as part of the morning ritual.
  const folded = cadence === 'explore' || cadence === 'setup'

  return (
    <nav className="ceo-cadence-nav" aria-label="Cadence">
      {TABS.map(({ id, label }) => {
        const items: CeoCadenceItem[] = cadences?.[id] ?? []
        const here = cadence === id
        const decide = items.filter((item) => item.kind === 'decide').length
        const watch = items.filter((item) => item.kind === 'watch').length
        return (
          <div key={id} className={`ceo-cad-group${here ? ' on' : ''}${folded ? ' folded' : ''}`}>
            <button
              type="button"
              className="ceo-cad"
              aria-current={here ? 'page' : undefined}
              onClick={() => onCadenceChange(id)}
            >
              {label}
              {decide > 0 ? (
                <span className="ceo-cad-count">{decide}</span>
              ) : watch > 0 ? (
                <span className="ceo-cad-count watch">{watch}</span>
              ) : null}
            </button>
            {folded ? null : (
              <div className="ceo-cad-chips">
                {items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`ceo-callout ${toneOf(item.kind)}`}
                    title={item.headline}
                    onClick={() => onJumpToItem(id, item.key)}
                  >
                    <span className="ceo-callout-n">{chipNumberFor(item)}</span>
                    <span className="ceo-callout-l">{chipLabelFor(item)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
      <div className={`ceo-cad-group${cadence === 'explore' ? ' on' : ''}`}>
        <button
          type="button"
          className="ceo-cad"
          aria-current={cadence === 'explore' ? 'page' : undefined}
          onClick={() => onCadenceChange('explore')}
        >
          Explore
        </button>
      </div>
    </nav>
  )
}

function toneOf(kind: CeoCadenceItem['kind']): string {
  if (kind === 'decide') return 'bad'
  if (kind === 'watch') return 'watch'
  return 'good'
}
